import { createApp } from 'vue'
import { createPinia } from 'pinia'

import App from '@/App.vue'
import { router } from '@/infrastructure/router'
import { i18n, getLocale } from '@/infrastructure/i18n'
import { useSessionStore } from '@/application/auth'
import { useRuntimeConfigStore } from '@/application/runtime/use-runtime-config'
import {
    APIKEYS_API_KEY,
    AUDIT_API_KEY,
    AUTH_API_KEY,
    CARBON_API_KEY,
    OIDC_LAUNCHER_KEY,
    PROVIDER_KEYS_API_KEY,
    ROUTES_API_KEY,
    RUNTIME_CONFIG_API_KEY,
    TEAMS_API_KEY,
    TENANTS_API_KEY,
    USERS_API_KEY,
} from '@/ports'
import { HttpAPIKeysApi } from '@/adapters/http/apikeys-api.http'
import { HttpAuditApi } from '@/adapters/http/audit-api.http'
import { HttpAuthApi } from '@/adapters/http/auth-api.http'
import { HttpCarbonApi } from '@/adapters/http/carbon-api.http'
import { HttpProviderKeysApi } from '@/adapters/http/provider-keys-api.http'
import { HttpRoutesApi } from '@/adapters/http/routes-api.http'
import { HttpRuntimeConfigApi } from '@/adapters/http/runtime-config-api.http'
import { HttpTeamsApi } from '@/adapters/http/teams-api.http'
import { HttpTenantsApi } from '@/adapters/http/tenants-api.http'
import { HttpUsersApi } from '@/adapters/http/users-api.http'
import { BrowserOidcLauncher } from '@/adapters/oidc/browser-oidc-launcher'

// Import unique des tokens CSS — disponibles globalement via :root.
// Doit précéder le mount pour que les composants P* trouvent les variables.
import '@/design-system/tokens/index.css'

/**
 * Composition root frontend PreLLM. Cf. spec §13.4.
 *
 * Ordre du wire (Sprint 8-B) :
 *  1. `createApp(App)` + pinia (stores) + router
 *  2. `app.provide(AUTH_API_KEY, new HttpAuthApi())`
 *     `app.provide(OIDC_LAUNCHER_KEY, new BrowserOidcLauncher())`
 *  3. `hydrateSession()` : appel /auth/me pour résoudre `status` AVANT
 *     le premier `router.beforeEach`. Ça évite que la première route
 *     authentifiée tombe sur `status: 'unknown'` et redirige par erreur.
 *  4. `app.mount('#app')`.
 *
 * Le hydrate est borné par un timeout côté HttpAuthApi (fetch sans
 * AbortController spécifique pour V1 — le browser TCP timeout par défaut
 * est suffisant ; à durcir Sprint 11 si besoin).
 */

async function bootstrap(): Promise<void> {
    const app = createApp(App)
    app.use(createPinia())
    app.use(i18n)
    // Reflète la locale détectée sur <html lang> (a11y + SEO).
    document.documentElement.setAttribute('lang', getLocale())

    // Adapters singletons — la portée est l'app entière, pas par tenant
    // (le tenant courant est dans le cookie / la session côté backend).
    const authApi = new HttpAuthApi()
    app.provide(AUTH_API_KEY, authApi)
    app.provide(OIDC_LAUNCHER_KEY, new BrowserOidcLauncher())
    app.provide(AUDIT_API_KEY, new HttpAuditApi())
    app.provide(APIKEYS_API_KEY, new HttpAPIKeysApi())
    app.provide(TENANTS_API_KEY, new HttpTenantsApi())
    app.provide(USERS_API_KEY, new HttpUsersApi())
    app.provide(TEAMS_API_KEY, new HttpTeamsApi())
    app.provide(ROUTES_API_KEY, new HttpRoutesApi())
    app.provide(PROVIDER_KEYS_API_KEY, new HttpProviderKeysApi())
    app.provide(CARBON_API_KEY, new HttpCarbonApi())
    const runtimeConfigApi = new HttpRuntimeConfigApi()
    app.provide(RUNTIME_CONFIG_API_KEY, runtimeConfigApi)

    // ⚠️ pinia stores ne sont accessibles qu'après `app.use(createPinia)`,
    // mais on peut les invoquer hors composant via `useXxxStore()` tant
    // qu'on a installé le plugin. On hydrate AVANT mount pour fixer
    // `status` à 'authenticated' | 'unauthenticated' (pas 'unknown' au
    // premier guard router).
    await hydrateSession(authApi)
    // Best-effort : runtime-config est gated par session auth, on le tente
    // après l'hydrate pour les users authentifiés. Échec silencieux
    // (visiteurs anon → store reste sur default).
    await hydrateRuntimeConfig(runtimeConfigApi)

    app.use(router)
    app.mount('#app')
}

async function hydrateSession(authApi: HttpAuthApi): Promise<void> {
    const store = useSessionStore()
    try {
        const sess = await authApi.me()
        store.setAuthenticated(sess)
    } catch {
        // Tout échec (401, 5xx, network) → unauthenticated. Le user
        // arrivera sur /login, où il pourra retry. Pas de bandeau
        // "backend down" en V1 — l'erreur est suffisante via le form.
        store.setUnauthenticated()
    }
}

async function hydrateRuntimeConfig(api: HttpRuntimeConfigApi): Promise<void> {
    const store = useRuntimeConfigStore()
    try {
        const cfg = await api.get()
        store.setConfig(cfg)
    } catch {
        // Best-effort, l'UI fallback sur window.location.origin.
    }
}

void bootstrap()
